import { Directive, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ChartConfiguration } from '@common-modules/shared/charts/model/chart/chart-configuration';
import { BaseCalculateResizableChildComponent } from '@common-modules/shared/core/responsive/base-calculate-resizable-child.component';
import { DateFormats } from '@common-modules/shared/localization/date-formats.enum';
import { LocalizationHelperService } from '@common-modules/shared/localization/localization-helper.service';
import { DateRange } from '@common-modules/shared/model/date/date-range';
import { IElementSize } from '@common-modules/shared/model/element-size';
import { LogService } from '@common-modules/shared/wlm-log/log.service';
import { SpinnerWrapperSettings } from '@common-modules/wlm-spinner/models/spinner-wrapper-settings';
import { Observable, ReplaySubject, startWith } from 'rxjs';
import { CustomizableChartComponent } from '../customizable-chart/customizable-chart.component';
import { EventsChartComponent } from '../events-chart/events-chart.component';
import { EventChartPosition } from '../events-chart/models/event-chart-position';
import { EventChartQueryDto } from '../events-chart/models/events-chart-query.dto';
import { ChartType } from '../models/chart-type.enum';
import { CustomizableChartParameters } from '../models/customizable-chart-parameters';
import { CustomizableChartSettings } from '../models/customizable-chart-settings';
import { GenericCartesianChartSettings } from '../models/generic-chart-settings/generic-cartesian-chart-settings';
import { GChartClickEvent } from '../models/generic-events/g-chart-click-event';
import { GChartDataZoomEvent } from '../models/generic-events/g-chart-data-zoom-event';

@Directive()
export abstract class BaseCustomizableChartComponent extends BaseCalculateResizableChildComponent {
  @ViewChild(EventsChartComponent) eventChart: EventsChartComponent;
  @ViewChild(CustomizableChartComponent) customizableChart: CustomizableChartComponent;

  @Output() chartClickEvent = new EventEmitter<GChartClickEvent>();
  @Output() chartConfigurationChange = new EventEmitter<ChartConfiguration>();

  protected _chartConfiguration: ChartConfiguration;
  public get chartConfiguration(): ChartConfiguration {
    return this._chartConfiguration;
  }
  @Input()
  public set chartConfiguration(value: ChartConfiguration) {
    this._chartConfiguration = value;
    this.eventChartQuery = this.getEventChartQuery(value);

    if (value) {
      this.setChartSettings(value);
      this.chartConfigurationChange.emit(value);
      this._changeDetectorRef.detectChanges();
    }
  }

  dimensionToCalculate: 'height' | 'width' = 'height';
  private readonly _eventsChartSizeChanges$ = new ReplaySubject<IElementSize>(1);

  eventChartQuery: EventChartQueryDto;
  chartSettings: CustomizableChartSettings;

  xAxisMin: Date;
  xAxisMax: Date;
  xAxisRange: DateRange;

  isLoading = false;
  spinnerWrapperSettings: SpinnerWrapperSettings = {
    isOpaque: true,
  };

  constructor(
    protected readonly _localization: LocalizationHelperService,
    protected readonly _logService: LogService,
  ) {
    super();
  }

  ngOnInit(): void {}

  onChartClick = (event) => this.chartClickEvent.next(event);

  onChartLoading(isLoading): void {
    this.isLoading = isLoading;
  }

  onChartDataZoom(chartDataZoom: GChartDataZoomEvent): void {
    if (!this.eventChart) {
      return;
    }
    const { start, end } = chartDataZoom;
    this.eventChart.position = new EventChartPosition({
      start,
      end,
    });
  }

  onEventChartSizeChange(size: IElementSize): void {
    this._eventsChartSizeChanges$.next(size);
  }

  onMainChartSettingsChange(settings: GenericCartesianChartSettings): void {
    if (!settings || !settings.xAxes.length) {
      return;
    }

    const min = settings.xAxes[0].min;
    const max = settings.xAxes[0].max;
    if (typeof (min as Date).getMonth === 'function') {
      this.xAxisMin = min as Date;
      this.xAxisMax = max as Date;
      this.xAxisRange = new DateRange(this.xAxisMin, this.xAxisMax);
    } else if (typeof min === 'string') {
      this.xAxisMin = this._localization.localStrToDate(min as string, DateFormats.DateTime);
      this.xAxisMax = this._localization.localStrToDate(max as string, DateFormats.DateTime);
      this.xAxisRange = new DateRange(this.xAxisMin, this.xAxisMax);
    } else {
      this._logService.error({
        msg: 'Min and max properties of the main chart are not bindable to the events chart.',
      });
    }
  }

  abstract onChartConfiguration();

  showDataPoints() {
    this.customizableChart?.showDataPoints();
  }

  exportChart() {
    this.customizableChart?.exportChart();
  }

  private setChartSettings(chartConfiguration: ChartConfiguration) {
    const { minimum, maximum } = chartConfiguration.horizontalAxis[0];

    this.chartSettings = new CustomizableChartSettings({
      chartType: ChartType.customizable,
      dataParameters: new CustomizableChartParameters({
        startDate: minimum,
        endDate: maximum,
        chartConfiguration: chartConfiguration,
      }),
    });
  }

  private getEventChartQuery(chartConfiguration: ChartConfiguration): EventChartQueryDto {
    if (!chartConfiguration) {
      return null;
    }

    const { eventsControlSettings, eventsQuery } = chartConfiguration;

    if (!eventsControlSettings && !eventsQuery) {
      return null;
    }

    return new EventChartQueryDto({
      ...(eventsQuery as EventChartQueryDto),
      categories: eventsControlSettings.categories,
    });
  }

  fixedSizes$(): Observable<IElementSize>[] {
    return [this._eventsChartSizeChanges$.asObservable().pipe(startWith(null))];
  }
}
